import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useAuth } from '@/hooks/use-auth';

const MAX_ALLOWED_ORIGINS = 25;

function parseAllowedDomains(domainList: string): string[] {
  const unique = new Set<string>();
  const expectedFormat = 'https://example.com';

  for (const [index, line] of domainList.split('\n').entries()) {
    const value = line.trim();
    if (!value) continue;

    try {
      const parsed = new URL(value);

      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        throw new Error('INVALID_PROTOCOL');
      }

      unique.add(parsed.origin);
    } catch {
      throw new Error(
        `Invalid allowed domain on line ${index + 1}. Use origin format like ${expectedFormat} (http/https only).`,
      );
    }
  }

  return Array.from(unique);
}

export function useWidgetSettings() {
  const { user } = useAuth();
  const canManageWidgets = user?.role === 'admin' || user?.role === 'owner';

  const [title, setTitle] = useState('Website Claim Form');
  const [allowedDomains, setAllowedDomains] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['claim-widgets'],
    queryFn: () => apiClient.claimWidgets.listClaimWidgets(),
    enabled: canManageWidgets,
  });

  const widgets = data?.widgets ?? [];

  const createMutation = useMutation({
    mutationFn: async () => {
      const parsedAllowedDomains = parseAllowedDomains(allowedDomains);

      if (parsedAllowedDomains.length === 0) {
        throw new Error('Add at least one allowed domain (e.g. https://example.com).');
      }

      if (parsedAllowedDomains.length > MAX_ALLOWED_ORIGINS) {
        throw new Error(`You can add at most ${MAX_ALLOWED_ORIGINS} allowed domains.`);
      }

      return apiClient.claimWidgets.createClaimWidget({
        createClaimWidgetRequest: {
          name: title.trim(),
          allowedOrigins: parsedAllowedDomains,
        },
      });
    },
    onSuccess: async (createdWidget) => {
      setGeneratedKey(createdWidget.publicKey);
      setError(null);
      await refetch();
    },
    onError: (mutationError) => {
      const fallback = 'Unable to create widget. Check title/domains and try again.';
      setError(mutationError instanceof Error ? mutationError.message : fallback);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (widgetId: number) =>
      apiClient.claimWidgets.deleteClaimWidget({ widgetId }),
    onSuccess: async () => {
      setError(null);
      await refetch();
    },
    onError: (mutationError) => {
      const fallback = 'Unable to delete embed. Please try again.';
      setError(mutationError instanceof Error ? mutationError.message : fallback);
    },
  });

  const createDisabled = useMemo(
    () => !canManageWidgets || createMutation.isPending || title.trim().length === 0 || allowedDomains.trim().length === 0,
    [allowedDomains, canManageWidgets, createMutation.isPending, title],
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    try {
      await createMutation.mutateAsync();
    } catch {
      // Error state is handled in onError.
    }
  };

  const handleDelete = async (widgetId: number) => {
    setError(null);

    try {
      await deleteMutation.mutateAsync(widgetId);
    } catch {
      // Error state is handled in onError.
    }
  };

  return {
    canManageWidgets,
    title,
    setTitle,
    allowedDomains,
    setAllowedDomains,
    generatedKey,
    error,
    isLoading,
    widgets,
    createDisabled,
    isCreating: createMutation.isPending,
    handleSubmit,
    handleDelete,
    deletingWidgetId: deleteMutation.isPending ? deleteMutation.variables ?? null : null,
  };
}
